import { useEffect, useRef, useState, useCallback } from 'react';
import { currentPushState, enablePush, disablePush, sendTestPush } from '../lib/pushApi';
import type { PushState } from '../lib/pushApi';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { NotificationView } from '../lib/types';

// relative time, compact
function ago(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationView[] | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get<{ count: number }>('/api/notifications/unread-count');
      setUnread(r.count);
    } catch { /* ignore transient */ }
  }, []);

  /**
   * ⚠ THIS POLL WAS BURNING YOUR DATABASE BUDGET.
   *
   * It ran every 30 seconds, from EVERY page (this component lives in AppShell), for EVERY logged-in
   * user, ALWAYS — including in a tab someone left open and forgot about.
   *
   * Neon's compute suspends after ~5 minutes idle. A 30-second poll means it NEVER idles. One person
   * with a background tab keeps the database awake — and billing — all day. That is how a free tier
   * reaches 94% with almost no real traffic.
   *
   * Two fixes:
   *   1. Poll every 2 minutes, not 30 seconds. Nobody needs a notification badge to be four seconds
   *      fresher than that.
   *   2. STOP ENTIRELY when the tab is hidden. A backgrounded tab has no business waking a database.
   *      It refreshes the moment the user comes back, so they never see a stale count.
   *
   * Together: roughly 4x fewer calls when active, and ZERO when nobody is looking.
   */
  useEffect(() => {
    loadCount();

    let timer: number | undefined;

    const start = () => {
      stop();
      timer = window.setInterval(loadCount, 120_000);   // 2 minutes
    };
    const stop = () => {
      if (timer !== undefined) { clearInterval(timer); timer = undefined; }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();                 // the tab is in the background — let the database sleep
      } else {
        loadCount();            // they're back: refresh immediately, then resume
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadCount]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const openPanel = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      setItems(null);
      try { setItems(await api.get<NotificationView[]>('/api/notifications?take=15')); }
      catch { setItems([]); }
    }
  };

  const markRead = async (n: NotificationView) => {
    if (!n.isRead) {
      try {
        await api.post(`/api/notifications/${n.id}/read`);
        setItems((cur) => cur?.map((x) => x.id === n.id ? { ...x, isRead: true } : x) ?? null);
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/read-all');
      setItems((cur) => cur?.map((x) => ({ ...x, isRead: true })) ?? null);
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button className="btn" onClick={openPanel} aria-label="Notifications" style={{ position: 'relative', padding: '9px 11px' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 999, background: 'var(--danger)', color: '#fff', fontSize: 10, fontWeight: 700,
            display: 'grid', placeItems: 'center', lineHeight: 1,
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="card" style={{
          position: 'absolute', right: 0, top: 46, width: 340, maxHeight: 460, overflowY: 'auto',
          zIndex: 60, padding: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
            <span style={{ fontWeight: 600 }}>Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ border: 'none', background: 'none', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Mark all read
              </button>
            )}
          </div>

          {items === null && <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>}
          {items?.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
              <div style={{ fontSize: 13 }}>You’re all caught up.</div>
            </div>
          )}
          <PhoneAlerts />

          {items?.map((n) => (
            <button key={n.id} onClick={() => markRead(n)} style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              borderBottom: '1px solid var(--line-2)', padding: '12px 16px', cursor: n.link ? 'pointer' : 'default',
              background: n.isRead ? 'transparent' : 'color-mix(in srgb, var(--accent) 8%, transparent)',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: n.isRead ? 500 : 700, fontSize: 14, color: 'var(--ink)' }}>{n.title}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{ago(n.createdAt)}</span>
              </div>
              {n.body && <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 3 }}>{n.body}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * "Also send these to my phone."
 *
 * ⚠ IT LIVES INSIDE THE BELL ON PURPOSE. A person who has just opened the notification panel is,
 * by definition, thinking about notifications; a switch buried in a settings page is one nobody
 * finds. It renders as a single quiet line until it has something to say.
 *
 * ⚠ EVERY STATE HERE IS A REAL ONE SOMEBODY WILL HIT, and the wrong message costs a support call:
 * an iPhone in a browser tab cannot do this at all until the app is on the home screen, a person
 * who once pressed Block cannot be un-blocked by us, and a school with no VAPID key configured
 * would otherwise get a switch that turns on and silently delivers nothing.
 */
function PhoneAlerts() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    currentPushState().then((s) => alive && setState(s)).catch(() => alive && setState('unsupported'));
    return () => { alive = false; };
  }, []);

  // Nothing to offer and nothing to explain — say nothing rather than adding furniture.
  if (state === null || state === 'unsupported' || state === 'unconfigured') return null;

  const line = { padding: '10px 16px', borderBottom: '1px solid var(--line-2)', fontSize: 12.5 };

  if (state === 'needs-install') {
    return (
      <div style={{ ...line, color: 'var(--ink-2)' }}>
        To get these on your phone, tap <strong>Share → Add to Home Screen</strong>, then open
        Eduvora from the home screen. Apple only allows alerts from an installed app.
      </div>
    );
  }

  if (state === 'blocked') {
    return (
      <div style={{ ...line, color: 'var(--ink-2)' }}>
        Notifications are blocked for this site in your browser settings. Only you can turn them
        back on there — Eduvora cannot ask again.
      </div>
    );
  }

  const on = state === 'on';

  async function toggle() {
    setBusy(true); setNote(null);
    try {
      if (on) {
        await disablePush();
        setState('off');
        setNote('Turned off on this device.');
      } else {
        const r = await enablePush();
        setNote(r.message);
        if (r.ok) setState('on');
      }
    } catch {
      setNote('That did not work. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      const reached = await sendTestPush();
      // ⚠ Reaching zero devices while the switch says on is the exact failure this button exists
      // to surface — a subscription the browser has quietly discarded.
      setNote(reached > 0
        ? `Sent to ${reached} ${reached === 1 ? 'device' : 'devices'}.`
        : 'No device received it. Turn alerts off and on again on this device.');
    } catch {
      setNote('The test could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={line}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: 'var(--ink-2)' }}>
          {on ? 'Alerts on this device' : 'Also alert me on this device'}
        </span>
        <span style={{ display: 'flex', gap: 10 }}>
          {on && (
            <button onClick={test} disabled={busy}
              style={{ border: 'none', background: 'none', color: 'var(--ink-3)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Test
            </button>
          )}
          <button onClick={toggle} disabled={busy}
            style={{ border: 'none', background: 'none', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {busy ? '…' : on ? 'Turn off' : 'Turn on'}
          </button>
        </span>
      </div>
      {note && <div style={{ color: 'var(--ink-3)', marginTop: 5, lineHeight: 1.5 }}>{note}</div>}
    </div>
  );
}
